import type { ColDef, ICellRendererParams } from '../../../components/ui/table';
import { Checkbox } from '../../../components/ui/checkbox';
import type { PermissionModuleRow } from '../api';
import {
  allowAllCheckedState,
  matrixCellForColumn,
  type CapKey,
  type MatrixColumnDef,
} from '../capabilities';

export type PermissionsGridContext = {
  matrixColumns: MatrixColumnDef[];
  onToggle: (moduleId: number, action: CapKey, value: boolean) => void;
  onToggleAllowAll: (moduleId: number, enable: boolean) => void;
};

function formatModuleName(name: string): string {
  return String(name ?? '')
    .trim()
    .toLowerCase()
    .replace(/\b(\w)/g, (char) => char.toUpperCase());
}

function ModuleCellRenderer(params: ICellRendererParams<PermissionModuleRow>) {
  const data = params.data;
  if (!data) return null;
  return (
    <div className="perm-matrix-module-meta">
      <span className="perm-matrix-module-name">{formatModuleName(data.moduleName)}</span>
      {data.overridden ? (
        <span className="perm-matrix-overridden">overridden</span>
      ) : null}
    </div>
  );
}

function AllowAllCellRenderer(params: ICellRendererParams<PermissionModuleRow>) {
  const ctx = params.context as PermissionsGridContext | undefined;
  const data = params.data;
  if (!data || !ctx) return null;
  const checked = allowAllCheckedState(data, data.moduleCode, ctx.matrixColumns);
  return (
    <div className="perm-matrix-check-wrap">
      <Checkbox
        checked={checked}
        onCheckedChange={(v) => ctx.onToggleAllowAll(data.moduleId, v === true)}
        aria-label={`Allow all permissions for ${data.moduleName}`}
      />
    </div>
  );
}

function PermissionCapCellRenderer(params: ICellRendererParams<PermissionModuleRow>) {
  const ctx = params.context as PermissionsGridContext | undefined;
  const data = params.data;
  const colId = params.column?.getColId();
  if (!data || !colId) return null;

  const cell = matrixCellForColumn(data.moduleCode, colId);
  if (cell.kind === 'na') {
    return (
      <span className="perm-matrix-na" aria-hidden>
        —
      </span>
    );
  }

  const tooltip = cell.hint ? `${cell.label}: ${cell.hint}` : undefined;
  return (
    <div className="perm-matrix-check-wrap" title={tooltip}>
      <Checkbox
        checked={!!data[cell.key]}
        onCheckedChange={(v) => ctx?.onToggle(data.moduleId, cell.key, v === true)}
        aria-label={`${cell.label} for ${data.moduleName}`}
      />
    </div>
  );
}

export const PERMISSIONS_GRID_DEFAULT_COL_DEF: ColDef<PermissionModuleRow> = {
  sortable: false,
  filter: false,
  resizable: true,
  suppressHeaderMenuButton: true,
  wrapHeaderText: false,
  autoHeaderHeight: false,
  cellClass: 'perm-matrix-ag-check-cell',
};

/** Single-line uppercase headers (grid header font + letter-spacing). */
function permissionColumnMinWidth(header: string): number {
  const chars = header.trim().length;
  return Math.max(80, Math.ceil(36 + chars * 10));
}

/** Fit longest module label + optional overridden badge on one line. */
export function estimateModuleColumnWidth(rows: PermissionModuleRow[]): number {
  let max = 300;
  for (const row of rows) {
    const name = formatModuleName(row.moduleName);
    let width = 48 + name.length * 8;
    if (row.overridden) width += 104;
    max = Math.max(max, width);
  }
  return Math.ceil(Math.min(max, 560));
}

export function createPermissionMatrixColumnDefs(
  matrixColumns: MatrixColumnDef[],
  moduleRows: PermissionModuleRow[] = [],
): ColDef<PermissionModuleRow>[] {
  const moduleWidth = estimateModuleColumnWidth(moduleRows);
  const capCols: ColDef<PermissionModuleRow>[] = matrixColumns.map((col) => {
    const minWidth = permissionColumnMinWidth(col.header);
    return {
      colId: col.id,
      headerName: col.header,
      minWidth,
      width: minWidth,
      cellRenderer: PermissionCapCellRenderer,
      headerClass: 'perm-matrix-ag-check-header',
      cellClass: 'perm-matrix-ag-check-cell',
    };
  });

  return [
    {
      colId: 'module',
      field: 'moduleName',
      headerName: 'Modules',
      pinned: 'left',
      minWidth: moduleWidth,
      width: moduleWidth,
      sortable: true,
      suppressSizeToFit: true,
      cellRenderer: ModuleCellRenderer,
      headerClass: 'perm-matrix-ag-module-header',
      cellClass: 'perm-matrix-ag-module-cell',
    },
    {
      colId: 'allowAll',
      headerName: 'Allow all',
      minWidth: permissionColumnMinWidth('Allow all'),
      width: permissionColumnMinWidth('Allow all'),
      cellRenderer: AllowAllCellRenderer,
      headerClass: 'perm-matrix-ag-check-header',
      cellClass: 'perm-matrix-ag-check-cell',
    },
    ...capCols,
  ];
}

export const permissionMatrixGridOptions = {
  getRowId: (params: { data: PermissionModuleRow }) => String(params.data.moduleId),
  suppressCellFocus: true,
  suppressMovableColumns: true,
};
